// ./src/components/features/Users/UserEditModal/UserEditModal.tsx
import React, { useState, useEffect } from "react";
import { TextInput, Stack, PasswordInput, Grid } from "@mantine/core";

import { User, UserInput } from "@/utils/api";
import { AppDrawer, StatusSelector } from "@/components/generic";

export interface UserEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** User to edit */
  user?: User | null;

  /** Callback when user should be updated */
  onUpdateUser?: (id: string, data: UserInput) => Promise<User | null>;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface FormData {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  repeatPassword: string;
  status: number | null;
}

const initialFormData: FormData = {
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  repeatPassword: "",
  status: 100,
};

export const UserEditModal: React.FC<UserEditModalProps> = ({
  opened,
  onClose,
  user,
  onUpdateUser,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null | undefined>
  >({});

  const isSubmitting = isUpdating;

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (opened) {
      if (user) {
        // Populate form with user data
        setFormData({
          firstName: user.firstName || "",
          middleName: user.middleName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: (user as any).phone || "",
          password: "",
          repeatPassword: "",
          status: user.status != null ? user.status : 100,
        });
      } else {
        setFormData(initialFormData);
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, user, onClearError]);

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // First name is required
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    // Last name is required
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    // Email is required and must be valid
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email.trim())) {
      errors.email = "Invalid email format";
    }

    // If password is being changed, validate it
    if (formData.password) {
      if (formData.password.length < 8) {
        errors.password = "Password must be at least 8 characters";
      }
      if (formData.password !== formData.repeatPassword) {
        errors.repeatPassword = "Passwords do not match";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (
    field: keyof FormData,
    value: string | number | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    if (!user?.id || !onUpdateUser) {
      return;
    }

    try {
      const submitData: UserInput = {
        firstName: formData.firstName.trim(),
        middleName: formData.middleName.trim() || null,
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        status: formData.status,
      };

      // Add phone if available
      if (formData.phone.trim()) {
        (submitData as any).phone = formData.phone.trim();
      }

      // Add password fields if they have values
      if (formData.password) {
        submitData.password = formData.password;
        submitData.repeatPassword = formData.repeatPassword;
      }

      const result = await onUpdateUser(user.id, submitData);

      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("User update error:", error);
    }
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title="Edit User"
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={lastError}
      actionType="save-cancel"
      onConfirm={handleSubmit}
    >
      <Stack gap="md">
        {/* Name Fields */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="First Name"
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(e) => handleFieldChange("firstName", e.target.value)}
              error={validationErrors.firstName}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              value={formData.lastName}
              onChange={(e) => handleFieldChange("lastName", e.target.value)}
              error={validationErrors.lastName}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Email and Phone */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) => handleFieldChange("email", e.target.value)}
              error={validationErrors.email}
              type="email"
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Phone"
              placeholder="Enter phone number"
              value={formData.phone}
              onChange={(e) => handleFieldChange("phone", e.target.value)}
              error={validationErrors.phone}
              type="tel"
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Password Fields */}
        <Grid>
          <Grid.Col span={12}>
            <PasswordInput
              label="New Password"
              placeholder="Leave empty to keep current password"
              value={formData.password}
              onChange={(e) => handleFieldChange("password", e.target.value)}
              error={validationErrors.password}
              description="Only fill this if you want to change the password"
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <PasswordInput
              label="Confirm New Password"
              placeholder="Re-enter new password"
              value={formData.repeatPassword}
              onChange={(e) =>
                handleFieldChange("repeatPassword", e.target.value)
              }
              error={validationErrors.repeatPassword}
              disabled={!formData.password}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Status */}
        <StatusSelector
          label="Status"
          value={formData.status}
          onChange={(value) => handleFieldChange("status", value)}
          size="md"
        />
      </Stack>
    </AppDrawer>
  );
};
